import { ColumnSort, SortingState } from "@tanstack/react-table";
import _ from "lodash";
import {
  decodeObject,
  encodeObject,
  searchStringToObject,
} from "serialize-query-params";

import { CARD_FILTERS_QS_KEY } from "../constants";
import {
  IFilterSchemaObject,
  INamedFilterSchemaObject,
  TFiltersQs,
  ValueType,
} from "../models";
import { Log } from "./log.helpers";
import { hasKeys } from "./objects.helpers";
import { hasFilterValue } from "./table.filters.helpers";

const serializeFilterValue = (
  val: ValueType | boolean | boolean[] | null[],
): string => {
  if (Array.isArray(val)) {
    return `~${val.join(",")}`;
  } else {
    return String(val);
  }
};

const deserializeSingleValue = (
  val: string | number | boolean,
): string | number | boolean | null => {
  if (val === "true") {
    return true;
  }

  if (val === "false") {
    return false;
  }

  if (+val) {
    return +val;
  }

  if (val === "") {
    return null;
  }

  return val;
};

const deserializeFilterValue = (
  val: string,
): ValueType | boolean | boolean[] | null => {
  if (val.startsWith("~")) {
    const valPure = val.slice(1);
    return valPure.split(",").map(x => deserializeSingleValue(x)) as
      number[] | string[] | boolean[];
  } else {
    return deserializeSingleValue(val);
  }
};

export const serializeQsFilterModel = (
  filter: IFilterSchemaObject,
): TFiltersQs | undefined => {
  if (!hasKeys(filter)) {
    return undefined;
  }

  const serialized: TFiltersQs = {};
  Object.entries(filter).forEach(([fieldId, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      !_.isEmpty(value)
    ) {
      serialized[fieldId] = serializeFilterValue(value);
    }
  });

  if (!hasKeys(serialized)) {
    return undefined;
  }

  Log.debug("serialize filter model", filter, serialized);
  return serialized;
};

/**
 * Same wire encoding as `serializeQsFilterModel`, but keyed by control name.
 * Used by deep links built on a page that can't resolve the target entity's
 * numeric control ids (see `INamedFilterSchemaObject`).
 */
export const serializeQsNamedFilterModel = (
  filter: INamedFilterSchemaObject,
): TFiltersQs | undefined => {
  if (!hasKeys(filter)) {
    return undefined;
  }

  const serialized: TFiltersQs = {};
  Object.entries(filter).forEach(([fieldName, value]) => {
    // Note this is NOT `_.isEmpty`, which is true for every scalar
    // (`_.isEmpty(5)`), silently dropping the single-value filters this object is
    // typed to carry.
    if (hasFilterValue(value)) {
      serialized[fieldName] = serializeFilterValue(value);
    }
  });

  if (!hasKeys(serialized)) {
    return undefined;
  }

  return serialized;
};

/**
 * The full `?cardFilters=...` search string for a deep link. Encoded exactly the
 * way `ObjectParam` decodes it, so the target page reads it back through the
 * normal query-param layer (see `useCardFiltersFromQs`). Empty string when there
 * is nothing to send, so callers can append it unconditionally.
 */
export const buildCardFiltersSearch = (
  filters: INamedFilterSchemaObject,
): string => {
  const serialized = serializeQsNamedFilterModel(filters);

  if (!serialized) {
    return "";
  }

  const encoded = encodeObject(serialized);

  if (!encoded) {
    return "";
  }

  return `?${new URLSearchParams({ [CARD_FILTERS_QS_KEY]: encoded }).toString()}`;
};

/** Inverse of `serializeQsNamedFilterModel`; keys stay names, so no numeric guard. */
export const deserializeQsNamedFilterModel = (
  filter: TFiltersQs,
): INamedFilterSchemaObject | undefined => {
  if (!hasKeys(filter)) {
    return undefined;
  }

  const deSerialized: INamedFilterSchemaObject = {};
  Object.entries(filter).forEach(([fieldName, value]) => {
    // `value` is the encoded string at this point, so an empty string is the only
    // "nothing here" case — `_.isEmpty` would reject every short string too.
    if (value !== undefined && value !== null && value !== "") {
      deSerialized[fieldName] = deserializeFilterValue(value) as
        ValueType | null | undefined;
    }
  });

  return deSerialized;
};

export const deserializeQsFilterModel = (
  filter: TFiltersQs,
): IFilterSchemaObject | undefined => {
  if (!hasKeys(filter)) {
    return undefined;
  }

  const deSerialized: IFilterSchemaObject = {};
  Object.entries(filter).forEach(([fieldId, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      +fieldId &&
      !_.isEmpty(value)
    ) {
      deSerialized[+fieldId] = deserializeFilterValue(value) as
        ValueType | null | undefined;
    }
  });

  return deSerialized;
};

const serializeSortingStateItem = (item: ColumnSort): string => {
  return `${item.id}-${item.desc ? "desc" : "asc"}`;
};

const deserializeSortingStateItem = (
  item: string | null,
): ColumnSort | undefined => {
  if (!item?.includes("-")) {
    return undefined;
  }

  const parsed = item.split("-");

  if (!parsed?.length || !parsed[0]) {
    return undefined;
  }

  const sort: ColumnSort = {
    id: parsed[0],
    desc: parsed[1] === "desc" || false,
  };
  return sort;
};

export const serializeSortingStateQs = (
  sortingState?: SortingState,
): (string | null)[] | undefined => {
  if (!sortingState?.length) {
    return undefined;
  }

  const sortingStateQs = sortingState?.map(x => serializeSortingStateItem(x));

  return sortingStateQs;
};

export const deserializeSortingStateQs = (
  sortingStateQs?: (string | null)[] | null,
): SortingState | undefined => {
  if (!sortingStateQs?.length) {
    return undefined;
  }

  const sortingState: SortingState = [];
  sortingStateQs?.forEach(x => {
    const item = deserializeSortingStateItem(x);

    if (item?.id) {
      sortingState.push(item);
    }
  });

  if (!sortingState.length) {
    return undefined;
  }

  return sortingState;
};

export const filterObjFromHash = (): IFilterSchemaObject | undefined => {
  const hash = window.location.hash;
  const queryPart = hash?.slice(hash.indexOf("?") + 1);

  if (!queryPart) {
    return undefined;
  }

  const decodedQuery = searchStringToObject(queryPart);

  if (!decodedQuery.filters) {
    return undefined;
  }

  const decodedFilterObject = decodeObject(decodedQuery.filters);

  if (!decodedFilterObject || !hasKeys(decodedFilterObject)) {
    return undefined;
  }

  const deserializedQs = deserializeQsFilterModel(
    decodedFilterObject as TFiltersQs,
  );
  return deserializedQs;
};
